import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiSave } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import { champFields, extractEntity, extractList as extractTournamentList } from '../utilities/tournamentsApi';
import { extractList, formFromDisqualificationItem, playerFullName } from '../utilities/disqualifications';
import AdminPlayerPicker from '../components/AdminPlayerPicker';
import CustomModal from '../components/CustomModal';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';
import '../styles/tournamentsManagement.css';

const emptyForm = {
  country_id: '',
  league_id: '',
  player: null,
  mode: 'matches',
  matches_amount: '1',
  to_date: '',
  comment: '',
};

export default function ManageDisqualification() {
  const { disqualificationId, playerId, leagueId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('disqualifications');
  const isAutoView = playerId != null;
  const isNew = !disqualificationId && !isAutoView;
  const isReadOnly = isAutoView || !canWrite;

  const [form, setForm] = useState(emptyForm);
  const [leagues, setLeagues] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countryLeagues, setCountryLeagues] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCountryLeagues, setLoadingCountryLeagues] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const availableIds = useMemo(
    () => new Set(leagues.map((item) => Number(item.id)).filter((id) => id !== 0)),
    [leagues]
  );

  const leaguesForCountry = useMemo(
    () => countryLeagues.filter((item) => availableIds.has(Number(item.id))),
    [countryLeagues, availableIds]
  );

  useEffect(() => {
    if (!champId && champsLoading) return;
    initPage();
  }, [disqualificationId, playerId, leagueId, champId, champsLoading]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const loadCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountries, champFields(champId));
      const list = extractTournamentList(response.data).map((item) => ({
        id: item.id,
        name: item.name || item.country_name || '—',
      }));
      setCountries(list);
      return list;
    } catch (error) {
      setCountries([]);
      throw error;
    } finally {
      setLoadingCountries(false);
    }
  };

  const loadLeaguesForCountry = async (countryId) => {
    if (!countryId) {
      setCountryLeagues([]);
      return [];
    }
    setLoadingCountryLeagues(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, { id: Number(countryId) });
      const next = extractTournamentList(extractEntity(response.data).leagues).map((item) => ({
        id: item.id || item.league_id,
        name: item.name || item.league_name || '—',
      }));
      setCountryLeagues(next);
      return next;
    } catch (error) {
      setCountryLeagues([]);
      throw error;
    } finally {
      setLoadingCountryLeagues(false);
    }
  };

  const initPage = async () => {
    setMessage({ text: '', type: '' });
    setErrors({});
    if (!champId) {
      setLoading(false);
      setMessage({ text: t('dqLoadError'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const leaguesResponse = await authorizedPost(dispatch, API_URLS.getAdminDisqualificationLeagues, {
        champ_id: champId,
      });
      let nextLeagues = extractList(leaguesResponse.data);

      if (isNew) {
        await loadCountries();
        setLeagues(nextLeagues);
        setCountryLeagues([]);
        setForm(emptyForm);
        setLoading(false);
        return;
      }

      if (isAutoView) {
        let item = location.state?.item;
        if (!item || Number(item.player_id) !== Number(playerId)) {
          const listResponse = await authorizedPost(dispatch, API_URLS.getAdminDisqualifications, {
            champ_id: champId,
            limit: 200,
            offset: 0,
          });
          const items = extractList(listResponse.data);
          item = items.find((row) => (
            Number(row.is_auto) === 1
            && Number(row.player_id) === Number(playerId)
            && Number(row.league_id || 0) === Number(leagueId || 0)
          ));
        }
        const nextForm = formFromDisqualificationItem(item);
        if (!nextForm) {
          setLeagues(nextLeagues);
          setMessage({ text: t('dqNotFound'), type: 'error' });
          setForm(emptyForm);
        } else {
          if (nextForm.tournament && !nextLeagues.some((row) => String(row.id) === String(nextForm.league_id))) {
            nextLeagues = [{ id: nextForm.league_id, name: nextForm.tournament }, ...nextLeagues];
          }
          setLeagues(nextLeagues);
          setForm({ ...nextForm, country_id: '' });
        }
        setCountries([]);
        setCountryLeagues([]);
        setLoading(false);
        return;
      }

      setLeagues(nextLeagues);
      setCountries([]);
      setCountryLeagues([]);
      const response = await authorizedPost(dispatch, API_URLS.getAdminDisqualification, {
        id: Number(disqualificationId),
      });
      const payload = response.data || {};
      const item = payload.id != null ? payload : (payload.data || payload.item || {});
      if (!item.id) {
        setMessage({ text: t('dqNotFound'), type: 'error' });
        setForm(emptyForm);
      } else {
        const player = item.player || {
          id: item.player_id,
          name: item.player_name,
          player_name: item.player_name,
        };
        const hasDate = Boolean(item.to_date) && !(item.matches_amount > 0);
        setForm({
          country_id: '',
          league_id: String(item.league_id == null ? 0 : item.league_id),
          player: {
            ...player,
            id: player.id || item.player_id,
            player_name: playerFullName(player, item.player_name),
          },
          mode: hasDate ? 'time' : 'matches',
          matches_amount: item.matches_amount == null ? '1' : String(item.matches_amount),
          to_date: item.to_date || '',
          comment: item.comment || item.reason || '',
        });
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('dqLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = async (countryId) => {
    setForm((prev) => ({
      ...prev,
      country_id: countryId,
      league_id: '',
      player: null,
    }));
    setErrors((prev) => ({ ...prev, country_id: '', league_id: '', player: '' }));
    setMessage({ text: '', type: '' });
    try {
      await loadLeaguesForCountry(countryId);
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('tsLoadLeaguesError')), type: 'error' });
    }
  };

  const handleLeagueChange = (nextId) => {
    updateField('league_id', nextId);
    if (isNew) updateField('player', null);
  };

  const validate = () => {
    const nextErrors = {};
    if (isNew && !form.country_id) nextErrors.country_id = t('twNeedCountry');
    if (form.league_id === '' || form.league_id == null) nextErrors.league_id = t('dqNeedTournament');
    if (!form.player || !form.player.id) nextErrors.player = t('dqNeedPlayer');
    if (form.mode === 'matches') {
      const matches = Number(form.matches_amount);
      if (!Number.isFinite(matches) || matches <= 0) nextErrors.matches_amount = t('dqInvalidMatches');
    } else if (!form.to_date) {
      nextErrors.to_date = t('dqNeedEndDate');
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = {
        champ_id: champId,
        league_id: Number(form.league_id),
        player_id: Number(form.player.id),
        comment: form.comment.trim() || undefined,
      };
      if (!isNew) payload.id = Number(disqualificationId);
      if (form.mode === 'matches') {
        payload.matches_amount = Number(form.matches_amount);
      } else {
        payload.to_date = form.to_date;
      }
      await authorizedPost(dispatch, API_URLS.addAdminDisqualification, payload);
      if (isNew) {
        history.push('/league_disqualifications');
      } else {
        setMessage({ text: t('dqSaved'), type: 'success' });
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, isNew ? t('dqAddError') : t('dqSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await authorizedPost(dispatch, API_URLS.removeAdminDisqualification, {
        id: Number(disqualificationId),
      });
      history.push('/league_disqualifications');
    } catch (error) {
      setDeleteVisible(false);
      setMessage({ text: getApiErrorMessage(error, t('dqRemoveError')), type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const canCreate = Boolean(form.country_id) && leaguesForCountry.length > 0 && form.league_id;

  if (loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page dq-edit-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs
                items={[
                  { label: t('dqTitle'), to: '/league_disqualifications' },
                  { label: isNew ? t('dqNewManual') : (isReadOnly ? t('dqAuto') : t('dqEditManual')) },
                ]}
              />
            </div>
          </div>
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('dqLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  const crumbLabel = isNew
    ? t('dqNewManual')
    : (playerFullName(form.player) || t('dqEditManual'));

  const leagueLabel = leagues.find((item) => String(item.id) === String(form.league_id))?.name
    || form.tournament
    || '—';

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page dq-edit-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs
              items={[
                { label: t('dqTitle'), to: '/league_disqualifications' },
                { label: crumbLabel },
              ]}
            />
          </div>
        </div>

        <div className="st-scroll">
          <div className="st-content">
            {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

            <div className="st-block">
              <div className="st-form-grid">
                {isNew ? (
                  <>
                    <div className="st-field st-field-wide">
                      <label>{t('gmCountry')} <span className="st-required">*</span></label>
                      {loadingCountries ? (
                        <p className="st-hint">{t('dqLoading')}</p>
                      ) : countries.length ? (
                        <select
                          className={`st-select ${errors.country_id ? 'error' : ''}`}
                          value={form.country_id}
                          onChange={(event) => handleCountryChange(event.target.value)}
                        >
                          <option value="">{t('cmCountryPh')}</option>
                          {countries.map((item) => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                      ) : (
                        <p className="st-hint">{t('twNoCountries')}</p>
                      )}
                      {errors.country_id ? <span className="st-field-error">{errors.country_id}</span> : null}
                    </div>

                    <div className="st-field st-field-wide">
                      <label>{t('dqLeague')} <span className="st-required">*</span></label>
                      {!form.country_id ? (
                        <p className="st-hint">{t('twPickCountryFirst')}</p>
                      ) : loadingCountryLeagues ? (
                        <p className="st-hint">{t('dqLoading')}</p>
                      ) : leaguesForCountry.length ? (
                        <select
                          className={`st-select ${errors.league_id ? 'error' : ''}`}
                          value={form.league_id}
                          onChange={(event) => handleLeagueChange(event.target.value)}
                        >
                          <option value="">{t('twLeaguePh')}</option>
                          {leaguesForCountry.map((item) => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                      ) : (
                        <p className="st-hint">{t('dqNoLeaguesInCountry')}</p>
                      )}
                      {errors.league_id ? <span className="st-field-error">{errors.league_id}</span> : null}
                    </div>
                  </>
                ) : (
                  <div className="st-field st-field-wide">
                    <label>{t('dqLeague')} <span className="st-required">*</span></label>
                    {isReadOnly ? (
                      <input className="st-input" value={leagueLabel} readOnly />
                    ) : (
                      <select
                        className={`st-select ${errors.league_id ? 'error' : ''}`}
                        value={form.league_id}
                        onChange={(event) => handleLeagueChange(event.target.value)}
                      >
                        {leagues.map((item) => (
                          <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                      </select>
                    )}
                    {errors.league_id ? <span className="st-field-error">{errors.league_id}</span> : null}
                  </div>
                )}

                <div className="st-field">
                  <label>{t('dqPlayer')} <span className="st-required">*</span></label>
                  <AdminPlayerPicker
                    champId={champId}
                    leagueId={form.league_id}
                    value={form.player}
                    error={errors.player}
                    disabled={isReadOnly || (isNew && !form.league_id)}
                    onChange={(player) => updateField('player', player)}
                  />
                  {errors.player ? <span className="st-field-error">{errors.player}</span> : null}
                </div>

                <div className="st-field">
                  <label>{t('dqMode')} <span className="st-required">*</span></label>
                  <select
                    className="st-select"
                    value={form.mode}
                    disabled={isReadOnly}
                    onChange={(event) => updateField('mode', event.target.value)}
                  >
                    <option value="matches">{t('dqModeMatches')}</option>
                    <option value="time">{t('dqModeTime')}</option>
                  </select>
                </div>

                {form.mode === 'matches' ? (
                  <div className="st-field">
                    <label>{t('dqGames')} <span className="st-required">*</span></label>
                    <input
                      type={isReadOnly ? 'text' : 'number'}
                      min="1"
                      className={`st-input ${errors.matches_amount ? 'error' : ''}`}
                      value={form.matches_amount}
                      readOnly={isReadOnly}
                      onChange={(event) => updateField('matches_amount', event.target.value)}
                    />
                    {errors.matches_amount ? <span className="st-field-error">{errors.matches_amount}</span> : null}
                  </div>
                ) : (
                  <div className="st-field">
                    <label>{t('dqUntilDate')} <span className="st-required">*</span></label>
                    <input
                      type="date"
                      className={`st-input ${errors.to_date ? 'error' : ''}`}
                      value={form.to_date}
                      readOnly={isReadOnly}
                      onChange={(event) => updateField('to_date', event.target.value)}
                    />
                    {errors.to_date ? <span className="st-field-error">{errors.to_date}</span> : null}
                  </div>
                )}

                <div className="st-field st-field-wide">
                  <label>{t('dqComment')}</label>
                  <textarea
                    className="st-textarea"
                    value={form.comment}
                    placeholder={t('dqCommentPh')}
                    readOnly={isReadOnly}
                    onChange={(event) => updateField('comment', event.target.value)}
                  />
                </div>
              </div>
              {isReadOnly ? <p className="st-hint dq-auto-note">{t('dqAutoReadonlyHint')}</p> : null}
            </div>
          </div>
        </div>

        {isReadOnly ? null : (
        <div className={`st-actions ${!isNew ? 'st-actions-split' : ''}`}>
          {!isNew ? (
            <button
              type="button"
              className="st-btn st-btn-danger"
              disabled={saving || deleting}
              onClick={() => setDeleteVisible(true)}
            >
              {t('delete')}
            </button>
          ) : <span />}
          <button
            type="button"
            className="st-btn st-btn-primary"
            disabled={saving || (isNew && !canCreate)}
            onClick={handleSave}
          >
            <FiSave /> {saving ? t('dqLoading') : (isNew ? t('dqAdd') : t('stdSaveChanges'))}
          </button>
        </div>
        )}
      </div>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={deleteVisible}
        closeModal={() => setDeleteVisible(false)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('dqDeleteTitle')}</div>
          <p>{t('dqDeleteConfirm')}</p>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setDeleteVisible(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-danger-modal" disabled={deleting} onClick={handleDelete}>
              {t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
